export default class TabsCards {
    constructor(element) {
        this.root = element;

        if (this.root.dataset.tabsCardsReady === '1') {
            return;
        }

        this.nav = this.root.querySelector('[data-tabs-cards-nav]');
        this.tabs = Array.from(this.root.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.root.querySelectorAll('[role="tabpanel"]'));
        this.indicator = this.root.querySelector('[data-tabs-cards-indicator]');
        this.hoverIndicator = this.root.querySelector('[data-tabs-cards-hover-indicator]');
        this.overlayLeft = this.root.querySelector('[data-tabs-cards-overlay-left]');
        this.overlayRight = this.root.querySelector('[data-tabs-cards-overlay-right]');
        this.hoveredIndex = -1;
        this.isTouching = false;

        if (!this.nav || !this.tabs.length || !this.panels.length) {
            return;
        }

        this.init();
    }

    init() {
        // Touch detection: suppress hover indicator during swipe
        this.nav.addEventListener('touchstart', () => { this.isTouching = true; }, { passive: true });
        this.nav.addEventListener('touchend', () => {
            setTimeout(() => { this.isTouching = false; }, 300);
        }, { passive: true });

        // Overlay scroll management
        this.nav.addEventListener('scroll', () => this.updateOverlays(), { passive: true });
        this.updateOverlays();

        // Tab events
        this.tabs.forEach((tab, index) => {
            tab.addEventListener('mouseenter', () => this.showHoverIndicator(index));
            tab.addEventListener('focus', () => this.showHoverIndicator(index));
            tab.addEventListener('click', () => this.setTabState(index, false));
            tab.addEventListener('keydown', (event) => this.handleKeydown(event, index));
        });

        this.nav.addEventListener('mouseleave', () => this.hideHoverIndicator());
        this.nav.addEventListener('focusout', (event) => {
            if (!this.nav.contains(event.relatedTarget)) {
                this.hideHoverIndicator();
            }
        });

        // Hide inactive panels via JS (all panels are visible
        // server-side so content is crawlable without JavaScript).
        this.panels.forEach((panel) => {
            if (panel.getAttribute('data-panel-index') !== '0') {
                panel.hidden = true;
            }
        });

        const initialIndex = this.tabs.findIndex((tab) =>
            tab.getAttribute('aria-selected') === 'true'
        );

        this.setTabState(initialIndex >= 0 ? initialIndex : 0, false);

        window.addEventListener('resize', () => {
            const activeIndex = this.tabs.findIndex((tab) =>
                tab.getAttribute('aria-selected') === 'true'
            );

            this.positionIndicator(this.indicator, activeIndex >= 0 ? activeIndex : 0);
            this.updateOverlays();

            if (this.hoveredIndex >= 0) {
                this.showHoverIndicator(this.hoveredIndex);
            }
        });

        this.root.dataset.tabsCardsReady = '1';
    }

    updateOverlays() {
        const sl = this.nav.scrollLeft;
        const maxScroll = this.nav.scrollWidth - this.nav.clientWidth;

        if (maxScroll <= 0) {
            if (this.overlayLeft) this.overlayLeft.style.opacity = '0';
            if (this.overlayRight) this.overlayRight.style.opacity = '0';
            return;
        }

        if (this.overlayLeft) this.overlayLeft.style.opacity = sl > 4 ? '1' : '0';
        if (this.overlayRight) this.overlayRight.style.opacity = sl < maxScroll - 4 ? '1' : '0';
    }

    positionIndicator(targetIndicator, index) {
        if (!targetIndicator || !this.tabs[index]) {
            return;
        }

        const tab = this.tabs[index];
        targetIndicator.style.width = tab.offsetWidth + 'px';
        targetIndicator.style.transform = 'translateX(' + tab.offsetLeft + 'px)';
    }

    showHoverIndicator(index) {
        if (this.isTouching || !this.hoverIndicator || !this.tabs[index]) {
            return;
        }

        this.hoveredIndex = index;
        this.positionIndicator(this.hoverIndicator, index);
        this.hoverIndicator.style.opacity = '1';
    }

    hideHoverIndicator() {
        this.hoveredIndex = -1;

        if (this.hoverIndicator) {
            this.hoverIndicator.style.opacity = '0';
        }
    }

    centerTab(index) {
        if (!this.nav || !this.tabs[index]) return;
        const tab = this.tabs[index];
        const scrollTarget = tab.offsetLeft - (this.nav.clientWidth / 2) + (tab.offsetWidth / 2);
        this.nav.scrollTo({ left: scrollTarget, behavior: 'smooth' });
    }

    setTabState(index, focusTab) {
        this.tabs.forEach((tab, tabIndex) => {
            const isActive = tabIndex === index;

            tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
            tab.setAttribute('tabindex', isActive ? '0' : '-1');

            if (focusTab && isActive) {
                tab.focus();
            }
        });

        this.positionIndicator(this.indicator, index);
        this.centerTab(index);

        this.panels.forEach((panel, panelIndex) => {
            panel.hidden = panelIndex !== index;

            // Re-trigger stagger animation on the newly shown panel
            if (panelIndex === index) {
                const staggerEl = panel.querySelector('[data-animate="stagger"]');
                if (staggerEl && staggerEl._replayStagger) {
                    staggerEl._replayStagger();
                }
            }
        });
    }

    handleKeydown(event, index) {
        let nextIndex = index;

        if (event.key === 'ArrowRight') {
            nextIndex = (index + 1) % this.tabs.length;
        } else if (event.key === 'ArrowLeft') {
            nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
        } else if (event.key === 'Home') {
            nextIndex = 0;
        } else if (event.key === 'End') {
            nextIndex = this.tabs.length - 1;
        } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.setTabState(index, false);
            return;
        } else {
            return;
        }

        event.preventDefault();
        this.setTabState(nextIndex, true);
    }
}
